import { inject, Injectable, Type } from '@angular/core';
import { SessionStore } from './session.store';
import { catchError, defaultIfEmpty, finalize, switchMap, take, tap, timeout } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { User } from '../models/user';
import { forkJoin, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { LoginModalComponent, LoginMode } from '@core/session/components/login-modal/login-modal.component';
import { UserModalComponent } from '@core/session/components/user-modal/user-modal.component';
import { SessionApiService } from '@backend/api/session/session-api.service';
import { LoginModalV2Component } from '@core/session/components/login-modal-v2/login-modal-v2.component';
import { FeatureTogglesService } from '@backend/api/feature-toggles/feature-toggles.service';

const BEFORE_LOGOUT_TIMEOUT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly sessionApi = inject(SessionApiService);
  private readonly featureToggles = inject(FeatureTogglesService);
  private readonly beforeLogoutHandlers = new Set<() => Observable<unknown>>();

  constructor(
    private sessionStore: SessionStore,
    private http: HttpClient,
    private router: Router,
    private nzModalService: NzModalService,
  ) {}

  auth(request: { login: string; password: string }) {
    return this.sessionApi.login(request.login, request.password).pipe(
      tap((result) => {
        this.sessionStore.update({ user: result.user, session: result });
      }),
    );
  }

  tryRestore() {
    return this.sessionApi.getCurrent().pipe(
      tap((result) => {
        this.sessionStore.update({ user: result.user, session: result });
      }),
    );
  }

  setTimeoutEnabled(timeoutEnabled: boolean) {
    this.sessionStore.update({
      timeoutEnabled,
    });
  }

  setCurrentDomain(currentDomain: number) {
    this.sessionStore.update({
      currentDomain,
    });
  }

  addObservableProcess(processId: number): Observable<User> {
    return this.http.put(`user/observable-processes/${processId}`, {}).pipe(
      tap((user: any) => {
        this.sessionStore.update({
          user: {
            ...this.sessionStore.getValue().user,
            observableProcessesIds: user.observableProcessesIds,
          },
        });
      }),
    );
  }

  deleteObservableProcess(processId: number): Observable<User> {
    return this.http.delete(`user/observable-processes/${processId}`).pipe(
      tap((user: any) => {
        this.sessionStore.update({
          user: {
            ...this.sessionStore.getValue().user,
            observableProcessesIds: user.observableProcessesIds,
          },
        });
      }),
    );
  }

  registerBeforeLogout(handler: () => Observable<unknown>): () => void {
    this.beforeLogoutHandlers.add(handler);

    return () => this.beforeLogoutHandlers.delete(handler);
  }

  private runBeforeLogoutHandlers(): Observable<unknown> {
    if (this.beforeLogoutHandlers.size === 0) {
      return of(null);
    }

    return forkJoin(
      [...this.beforeLogoutHandlers].map((handler) =>
        handler().pipe(
          take(1),
          timeout(BEFORE_LOGOUT_TIMEOUT_MS),
          catchError(() => of(null)),
          defaultIfEmpty(null),
        ),
      ),
    );
  }

  logout() {
    return this.runBeforeLogoutHandlers().pipe(
      switchMap(() => this.sessionApi.logout()),
      finalize(() => {
        this.sessionStore.update({
          user: null,
          session: null,
        });
        this.openLoginModal('login');
        this.router.navigate(['/']);
      }),
    );
  }

  openLoginModal(loginMode: LoginMode): Promise<User> {
    if (loginMode == 'timeout') {
      this.sessionStore.update({
        timeoutEnabled: false,
      });
      localStorage.removeItem('session:basicCreds');
    }
    // Флаг читается из снимка в localStorage: эндпоинт feature toggle требует сессию,
    // а окно входа рисуется до неё. На новой машине первый вход пройдёт в старом виде.
    const newDesign = this.featureToggles.newDesignEnabled$();
    const content: Type<LoginModalComponent | LoginModalV2Component> = newDesign
      ? LoginModalV2Component
      : LoginModalComponent;

    if (!LoginModalComponent.LoginModalOpened && !LoginModalV2Component.LoginModalOpened) {
      let options = {
        // в новом виде окно уже двух полей: 670px на логин и пароль — половина экрана
        nzWidth: newDesign ? 420 : 670,
        nzClosable: true,
        nzKeyboard: true,
        nzMaskClosable: false,
      };

      if (loginMode == 'timeout' || loginMode == 'login') {
        options.nzClosable = false;
        options.nzKeyboard = false;
      }

      return new Promise((resolve) => {
        this.nzModalService.create({
          // в новом виде шапка своя: логотип, режим и организация — заголовок модалки лишний
          nzTitle: newDesign ? null : 'Авторизация',
          nzFooter: newDesign ? null : undefined,
          nzContent: content,
          nzData: {
            submittedCallback: (user: User) => resolve(user),
            loginMode,
          },
          ...options,
        });
      });
    }
  }

  openUserModal() {
    this.nzModalService.create({
      nzTitle: 'Текущий пользователь',
      nzContent: UserModalComponent,
    });
  }

  upsert(user: User) {
    this.sessionStore.update({
      user,
    });
  }

  toggleDebugMode(state?: boolean) {
    this.sessionStore.update({
      debugMode: state === true || state === false ? state : !this.sessionStore.getValue().debugMode,
    });
  }
}
